import { useState, useEffect } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Calendar, CreditCard, Loader2, Sparkles, ShieldCheck, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import { isMobileBuild } from '@/lib/mobileBuild';
import UpgradeOnWebNotice from '@/components/UpgradeOnWebNotice';

import { stripePromise } from '@/lib/stripe';
import { useTaxEnabled, billingAddressFor, BillingZipField } from '@/components/BillingZip';

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
};

// ─── Payment Form (inner, wrapped with Elements) ─────────────────────────────

const PaymentForm = ({ plan, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/dashboard/practitioner-hub?tab=subscription` },
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message || 'Payment could not be processed.');
      setProcessing(false);
      return;
    }
    toast.success('Subscription activated!');
    onSuccess();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="practitioner-sub-payment-form">
      <PaymentElement />
      {error && (
        <p className="text-sm text-red-600" data-testid="practitioner-sub-payment-error">{error}</p>
      )}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={processing}
          data-testid="practitioner-sub-cancel-payment"
        >
          Back
        </Button>
        <Button
          type="submit"
          disabled={!stripe || processing}
          className="flex-1 bg-cyan-600 hover:bg-cyan-700"
          data-testid="practitioner-sub-confirm-payment"
        >
          {processing ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing…</>
          ) : (
            <>Subscribe for ${plan === 'yearly' ? 120 : 15} / {plan === 'yearly' ? 'year' : 'month'}</>
          )}
        </Button>
      </div>
    </form>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const PractitionerSubscription = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [zip, setZip] = useState('');
  const taxEnabled = useTaxEnabled();

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/practitioner/subscription/status`, { headers: authHeader() });
      setStatus(data);
    } catch (e) {
      toast.error('Could not load subscription status.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleSelectPlan = async (plan) => {
    setStarting(true);
    setPendingPlan(plan);
    try {
      const { data } = await axios.post(
        `${API}/practitioner/subscription/start`,
        { plan, billing_address: billingAddressFor(zip) },
        { headers: authHeader() }
      );
      setClientSecret(data.client_secret);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start subscription.');
      setPendingPlan(null);
    } finally {
      setStarting(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel auto-renewal? You keep access until the end of the current period.')) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/practitioner/subscription/cancel`, {}, { headers: authHeader() });
      toast.success('Auto-renewal cancelled. Your subscription stays active until the period ends.');
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not cancel subscription.');
    } finally {
      setCancelling(false);
    }
  };

  const handlePaymentSuccess = async () => {
    setClientSecret(null);
    setPendingPlan(null);
    await fetchStatus();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400" data-testid="practitioner-sub-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading subscription…
      </div>
    );
  }

  // ── Payment step ──
  if (clientSecret && pendingPlan) {
    return (
      <Card className="bg-slate-800/50 border-slate-700 max-w-xl mx-auto" data-testid="practitioner-sub-payment-card">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-cyan-400" />
            Activate your Booking Subscription
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'night' } }}>
            <PaymentForm
              plan={pendingPlan}
              onSuccess={handlePaymentSuccess}
              onCancel={() => { setClientSecret(null); setPendingPlan(null); }}
            />
          </Elements>
        </CardContent>
      </Card>
    );
  }

  const active = status?.active;
  const plan = status?.plan;
  const cancelAtEnd = status?.cancel_at_period_end;
  // Pre-launch: the gate is open for everyone and there is nothing to buy yet, so we say so
  // plainly rather than showing a "plan active" card for a plan nobody bought.
  const prelaunchFree = status?.prelaunch_free && !plan;

  return (
    <div className="space-y-5" data-testid="practitioner-subscription-page">
      <div>
        <h2 className="text-2xl font-bold text-white">Booking Subscription</h2>
        <p className="text-sm text-gray-400 mt-1">
          Required to list and accept in-app bookings. External booking URLs (Calendly, Acuity, etc.) remain free.
        </p>
      </div>

      {prelaunchFree && (
        <Card className="bg-amber-900/20 border-amber-700/50" data-testid="practitioner-sub-prelaunch-card">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-6 h-6 text-amber-300 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-semibold">Free while we're in pre-launch</p>
                <p className="text-sm text-gray-300 mt-1 leading-relaxed">
                  Build your availability, session types, client emails and booking page now —
                  there's nothing to pay and nothing to set up.
                  {status?.prelaunch_until
                    ? ` We open on ${formatDate(status.prelaunch_until)}, and we'll email you that day about starting your subscription.`
                    : " When we open, we'll email you about starting your subscription."}
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  Nothing is charged today and no card is needed.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active state */}
      {active && !prelaunchFree && (
        <Card className="bg-emerald-900/20 border-emerald-700/50" data-testid="practitioner-sub-active-card">
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-white font-semibold">
                  {plan === 'yearly' ? 'Yearly' : 'Monthly'} plan active
                </p>
                <p className="text-sm text-gray-300 mt-1">
                  {cancelAtEnd ? (
                    <>Auto-renewal is <span className="text-amber-400 font-medium">off</span>. Access ends {formatDate(status.current_period_end)}.</>
                  ) : (
                    <>
                      {plan === 'yearly'
                        ? `Auto-renews on ${formatDate(status.current_period_end)} for $120. We\u2019ll email you 7 days before.`
                        : `Renews monthly on ${formatDate(status.current_period_end)} for $15.`}
                    </>
                  )}
                </p>
              </div>
              {!cancelAtEnd && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  disabled={cancelling}
                  className="border-red-500/40 text-red-300 hover:bg-red-900/30 hover:text-red-200"
                  data-testid="practitioner-sub-cancel-btn"
                >
                  {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><XCircle className="w-4 h-4 mr-1" /> Cancel auto-renew</>)}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mobile: digital subscription purchase is handled on the website */}
      {(!active || cancelAtEnd) && isMobileBuild() && (
        <UpgradeOnWebNotice
          title="Manage your subscription on our website"
          description="Booking subscriptions are handled securely on our website. Subscribe there once, then manage everything right here in the app."
          ctaLabel="Subscribe on Website"
          path="/dashboard/practitioner-hub?tab=subscription"
          testId="practitioner-sub-web-notice"
        />
      )}

      {/* Sales tax needs a billing ZIP before Stripe will create the subscription */}
      {(!active || cancelAtEnd) && !isMobileBuild() && taxEnabled && (
        <Card className="bg-slate-800/50 border-slate-700" data-testid="practitioner-sub-billing-zip-card">
          <CardContent className="pt-6">
            <BillingZipField value={zip} onChange={setZip} theme="dark" testId="practitioner-sub-billing-zip" />
          </CardContent>
        </Card>
      )}

      {/* Plan picker — shown when inactive, or also if cancel-at-end so they can re-enable */}
      {(!active || cancelAtEnd) && !isMobileBuild() && (
        <div className="grid md:grid-cols-2 gap-4">
          {/* Monthly */}
          <Card className="bg-slate-800/50 border-slate-700 hover:border-cyan-600/50 transition-colors" data-testid="practitioner-sub-plan-monthly">
            <CardHeader>
              <div className="flex items-baseline justify-between">
                <CardTitle className="text-white text-lg">Monthly</CardTitle>
                <Calendar className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="mt-2">
                <span className="text-4xl font-bold text-white">$15</span>
                <span className="text-gray-400 text-sm ml-1">/month</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <ul className="text-sm text-gray-300 space-y-2">
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /> In-app bookings enabled</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /> 0% platform fee on service revenue</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /> Cancel any time</li>
              </ul>
              <Button
                className="w-full bg-cyan-600 hover:bg-cyan-700"
                disabled={starting}
                onClick={() => handleSelectPlan('monthly')}
                data-testid="practitioner-sub-select-monthly"
              >
                {starting && pendingPlan === 'monthly' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Subscribe'}
              </Button>
            </CardContent>
          </Card>

          {/* Yearly */}
          <Card className="bg-slate-800/50 border-amber-600/60 relative hover:border-amber-500 transition-colors" data-testid="practitioner-sub-plan-yearly">
            <Badge className="absolute -top-3 right-4 bg-amber-500 hover:bg-amber-500 text-slate-900 border-0 shadow-lg" data-testid="practitioner-sub-yearly-badge">
              <Sparkles className="w-3 h-3 mr-1" /> Best Value — Save $60
            </Badge>
            <CardHeader>
              <div className="flex items-baseline justify-between">
                <CardTitle className="text-white text-lg">Yearly</CardTitle>
                <Calendar className="w-5 h-5 text-amber-400" />
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-4xl font-bold text-white">$120</span>
                <span className="text-gray-400 text-sm">/year</span>
                <span className="text-gray-500 text-sm line-through ml-2" data-testid="practitioner-sub-yearly-strike">$180</span>
              </div>
              <p className="text-xs text-amber-300 mt-1">33% off vs monthly</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <ul className="text-sm text-gray-300 space-y-2">
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /> Everything in Monthly</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /> 2 months free</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /> Renewal reminder sent 7 days out</li>
              </ul>
              <Button
                className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                disabled={starting}
                onClick={() => handleSelectPlan('yearly')}
                data-testid="practitioner-sub-select-yearly"
              >
                {starting && pendingPlan === 'yearly' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Subscribe yearly'}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="mt-8 rounded-lg border border-slate-700/60 bg-slate-800/40 p-4" data-testid="practitioner-sub-fee-note">
        <p className="text-xs text-gray-300 leading-relaxed max-w-3xl">
          A 3% + $0.30 processing fee is added to the client&apos;s payment at booking — this covers
          Stripe&apos;s processing cost, so you receive the full listed service price. If a session is
          refunded, Stripe keeps its original processing fee and <strong className="text-gray-100">we
          absorb that</strong> — it is never charged back to you.
        </p>
      </div>
    </div>
  );
};

export default PractitionerSubscription;
